import { KeySet } from '@tableau/data-structures';
import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import React from 'react';
import { defaultMemoize } from 'reselect';
import {
  getFirstParamValueAsSet,
  paramValuesMapToSearch,
  removeParamValue,
  searchToParamValuesMap,
  setParamValues,
  UrlSearch,
  UrlSearchProvider
} from '../../../data-catalog-shared';

export function constructNewSearchForFieldSelection(search: string, fieldId: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const fields = paramValuesMap[CatalogUrlParamKeys.field];
  if (fields && fields.indexOf(fieldId) >= 0) {
    removeParamValue(paramValuesMap, CatalogUrlParamKeys.field, fieldId);
  } else {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.field, [fieldId]);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export function constructNewSearchWithClearedFieldSelection(search: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const fields = paramValuesMap[CatalogUrlParamKeys.field];
  if (fields) {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.field, []);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export type DatasourceSubplaceParams = {
  readonly urlSearchValue: string;
  readonly selectedFields: KeySet;
  readonly updateFieldSelection: (fieldId: string) => void;
  readonly clearFieldSelection: () => void;
};

export type DatasourceSubplaceParamsProviderProps = {
  readonly children: (params: DatasourceSubplaceParams) => React.ReactNode;
};

export class DatasourceSubplaceParamsProvider extends React.Component<DatasourceSubplaceParamsProviderProps> {

  public override render() {
    return (
      <UrlSearchProvider>
        {urlSearch =>
          <DatasourceSubplaceParamsProviderInternal urlSearch={urlSearch}>
            {this.props.children}
          </DatasourceSubplaceParamsProviderInternal>
        }
      </UrlSearchProvider>
    );
  }

}

type DatasourceSubplaceParamsProviderInternalProps = {
  readonly urlSearch: UrlSearch;
  readonly children: (params: DatasourceSubplaceParams) => React.ReactNode;
};

class DatasourceSubplaceParamsProviderInternal extends React.Component<DatasourceSubplaceParamsProviderInternalProps> {

  private readonly memoizedGetFirstParamValueAsSet = defaultMemoize(getFirstParamValueAsSet);

  public override render() {
    return this.props.children({
      urlSearchValue: this.props.urlSearch.value,
      selectedFields: this.memoizedGetFirstParamValueAsSet(this.props.urlSearch.value, CatalogUrlParamKeys.field),
      updateFieldSelection: this.updateFieldSelection,
      clearFieldSelection: this.clearFieldSelection,
    });
  }

  private readonly clearFieldSelection = () => {
    const urlSearch = this.props.urlSearch;
    const newSearchWithClearedFieldSelection = constructNewSearchWithClearedFieldSelection(urlSearch.value);
    urlSearch.update(newSearchWithClearedFieldSelection);
  }

  private readonly updateFieldSelection = (fieldId: string) => {
    const urlSearch = this.props.urlSearch;
    const newSearch = constructNewSearchForFieldSelection(urlSearch.value, fieldId);
    urlSearch.update(newSearch);
  }

}
